import { useEffect } from 'react';

interface PageMetaOptions {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  ogType?: 'website' | 'article';
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  articleMeta?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    section?: string;
    tags?: string[];
  };
}

const META_TAG_PREFIX = 'data-page-meta';

function setMeta(selector: string, attr: string, value: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${selector}]`);
  if (!el) {
    el = document.createElement('meta');
    const [k, v] = selector.split('=');
    el.setAttribute(k, v.replace(/"/g, ''));
    el.setAttribute(META_TAG_PREFIX, '1');
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

function setLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"][${META_TAG_PREFIX}]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    el.setAttribute(META_TAG_PREFIX, '1');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function setJsonLd(payload: Record<string, unknown> | Record<string, unknown>[]) {
  document.head.querySelectorAll(`script[type="application/ld+json"][${META_TAG_PREFIX}]`).forEach((n) => n.remove());
  const items = Array.isArray(payload) ? payload : [payload];
  for (const item of items) {
    const s = document.createElement('script');
    s.type = 'application/ld+json';
    s.setAttribute(META_TAG_PREFIX, '1');
    s.text = JSON.stringify(item);
    document.head.appendChild(s);
  }
}

export function usePageMeta(opts: PageMetaOptions) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = opts.title;

    setMeta('name="description"', 'content', opts.description);
    if (opts.noindex) setMeta('name="robots"', 'content', 'noindex,nofollow');
    setMeta('property="og:title"', 'content', opts.title);
    setMeta('property="og:description"', 'content', opts.description);
    setMeta('property="og:type"', 'content', opts.ogType ?? 'website');
    if (opts.canonical) {
      setMeta('property="og:url"', 'content', opts.canonical);
      setLink('canonical', opts.canonical);
    }
    setMeta('property="og:image"', 'content', opts.ogImage ?? 'https://serac.build/og-image.png');
    setMeta('name="twitter:title"', 'content', opts.title);
    setMeta('name="twitter:description"', 'content', opts.description);
    setMeta('name="twitter:image"', 'content', opts.ogImage ?? 'https://serac.build/og-image.png');

    if (opts.articleMeta) {
      if (opts.articleMeta.publishedTime) setMeta('property="article:published_time"', 'content', opts.articleMeta.publishedTime);
      if (opts.articleMeta.modifiedTime) setMeta('property="article:modified_time"', 'content', opts.articleMeta.modifiedTime);
      if (opts.articleMeta.author) setMeta('property="article:author"', 'content', opts.articleMeta.author);
      if (opts.articleMeta.section) setMeta('property="article:section"', 'content', opts.articleMeta.section);
      if (opts.articleMeta.tags) {
        document.head.querySelectorAll(`meta[property="article:tag"][${META_TAG_PREFIX}]`).forEach((n) => n.remove());
        for (const tag of opts.articleMeta.tags) {
          const m = document.createElement('meta');
          m.setAttribute('property', 'article:tag');
          m.setAttribute('content', tag);
          m.setAttribute(META_TAG_PREFIX, '1');
          document.head.appendChild(m);
        }
      }
    }

    if (opts.jsonLd) setJsonLd(opts.jsonLd);

    return () => {
      document.title = prevTitle;
      document.head.querySelectorAll(`[${META_TAG_PREFIX}]`).forEach((n) => n.remove());
    };
  }, [opts.title, opts.description, opts.canonical, opts.ogImage, opts.ogType, opts.noindex, JSON.stringify(opts.jsonLd), JSON.stringify(opts.articleMeta)]); // eslint-disable-line react-hooks/exhaustive-deps
}
